import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { Dialog, Typography, Button } from 'src/components';
import { useDeleteTeam } from 'src/hooks/teams/useDeleteTeam';

interface Team {
  id: number;
  name: string;
}

interface DeleteTeamModalProps {
  team: Team | null;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const DeleteTeamModal: React.FC<DeleteTeamModalProps> = ({
  team,
  open,
  onClose,
  onSuccess,
}) => {
  const { deleteTeam, isDeleting, error } = useDeleteTeam();

  const handleDelete = async () => {
    if (!team) return;

    const success = await deleteTeam(team.id.toString());
    if (success) {
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    }
  };

  if (!team) return null;

  return (
    <Dialog
      isOpen={open}
      onClose={onClose}
      size="lg"
      className="w-full max-w-md"
    >
      <Dialog.Header>
        <Typography variant="h4" className="text-neutral-800">
          Delete Team
        </Typography>
      </Dialog.Header>

      <Dialog.Content>
        <div className="p-4 flex flex-col gap-4">
          <Typography className="text-neutral-700">
            Are you sure you want to delete the team{' '}
            <strong>{team.name}</strong>?
          </Typography>

          <div className="bg-red-50 border flex flex-col border-red-200 rounded-md p-4 mb-4">
            <Typography className="text-red-600 text-sm">
              <Icon
                icon={faExclamationTriangle}
                className="text-red-500 pr-1"
              />
              This action cannot be undone. All team members will lose access to
              any data associated with this team.
            </Typography>
          </div>

          {error && (
            <Typography className="text-red-600 text-sm mt-2">
              {error.message || 'An error occurred while deleting the team.'}
            </Typography>
          )}
        </div>
      </Dialog.Content>

      <Dialog.Actions>
        <div className="flex justify-end gap-2 w-full">
          <Button
            variant="outlined"
            color="neutral"
            onClick={onClose}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            color="danger"
            onClick={handleDelete}
            loading={isDeleting}
            data-testid="confirm-delete-team-button"
          >
            Delete Team
          </Button>
        </div>
      </Dialog.Actions>
    </Dialog>
  );
};

export default DeleteTeamModal;
